import { Response } from '../lib/types/response';
import { CoinGeckoAssetData, CoinGeckoPriceData, PriceData } from '../types';
import { ApiCache } from '../lib/cache';

export class PriceService {
  private static instance: PriceService;
  private apiCache: ApiCache;

  private constructor() {
    // Initialize cache with refresh callbacks
    this.apiCache = new ApiCache(
      this.refreshPricesFromApi.bind(this),
      this.refreshAssetDetailsFromApi.bind(this)
    );
  }

  static getInstance(): PriceService {
    if (!PriceService.instance) {
      PriceService.instance = new PriceService();
    }
    return PriceService.instance;
  }

  /**
   * Get current prices for given asset IDs (with caching)
   */
  async getCurrentPrices(assetIds: string[]): Promise<Response<PriceData>> {
    if (assetIds.length === 0) {
      return {};
    }

    try {
      // Get cached prices
      const cachedPrices = await this.apiCache.priceCache.getPrices(assetIds);
      const missingAssetIds = await this.apiCache.priceCache.getMissingAssetIds(assetIds);
      
      // If all prices are cached, return them
      if (missingAssetIds.length === 0) {
        return cachedPrices;
      }

      // Fetch missing prices from API
      const fetchResult = await this.fetchCurrentPricesFromApi(missingAssetIds);
      if (fetchResult instanceof Error) {
        // Return cached prices even if API fails
        return Object.keys(cachedPrices).length > 0 ? cachedPrices : fetchResult;
      }

      // Cache the new prices
      await this.apiCache.priceCache.setPrices(fetchResult);

      // Combine cached and new prices
      return { ...cachedPrices, ...fetchResult };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Error getting prices: ${message}`);
    }
  }

  /**
   * Fetch current prices for given asset IDs from CoinGecko (direct API call)
   */
  async fetchCurrentPricesFromApi(assetIds: string[]): Promise<Response<PriceData>> {
    if (assetIds.length === 0) {
      return {};
    }

    try {
      const idsString = assetIds.join(',');
      const response = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${idsString}&vs_currencies=usd`);
      
      if (!response.ok) {
        return new Error(`Failed to fetch prices: HTTP ${response.status}`);
      }

      const prices: CoinGeckoPriceData = await response.json();
      const priceData: PriceData = {};
      
      assetIds.forEach(id => {
        priceData[id] = prices[id]?.usd || 0;
      });

      return priceData;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Error fetching prices: ${message}`);
    }
  }

  /**
   * Legacy method for backward compatibility
   */
  async fetchCurrentPrices(assetIds: string[]): Promise<Response<PriceData>> {
    return this.getCurrentPrices(assetIds);
  }

  /**
   * Get asset details (with caching)
   */
  async getAssetDetails(assetId: string): Promise<Response<CoinGeckoAssetData>> {
    try {
      // Check cache first
      const cachedDetails = await this.apiCache.assetDetailsCache.getAssetDetails(assetId);
      if (cachedDetails) {
        return cachedDetails;
      }

      // Fetch from API if not cached
      const fetchResult = await this.fetchAssetDetailsFromApi(assetId);
      if (fetchResult instanceof Error) {
        return fetchResult;
      }

      // Cache the result
      await this.apiCache.assetDetailsCache.setAssetDetails(assetId, fetchResult);
      return fetchResult;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Error getting asset details for ${assetId}: ${message}`);
    }
  }

  /**
   * Fetch asset details from CoinGecko (direct API call)
   */
  async fetchAssetDetailsFromApi(assetId: string): Promise<Response<CoinGeckoAssetData>> {
    try {
      const response = await this.fetchWithRetry(`https://api.coingecko.com/api/v3/coins/${assetId}`);
      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to fetch asset details for ${assetId}: ${message}`);
    }
  }

  /**
   * Legacy method for backward compatibility
   */
  async fetchAssetDetails(assetId: string): Promise<Response<CoinGeckoAssetData>> {
    return this.getAssetDetails(assetId);
  }

  /**
   * Refresh callback for price cache
   */
  private async refreshPricesFromApi(assetIds: string[]): Promise<Record<string, number>> {
    const result = await this.fetchCurrentPricesFromApi(assetIds);
    if (result instanceof Error) {
      console.error('Failed to refresh prices:', result.message);
      return {};
    }
    return result;
  }

  /**
   * Refresh callback for asset details cache
   */
  private async refreshAssetDetailsFromApi(assetIds: string[]): Promise<Record<string, CoinGeckoAssetData>> {
    const refreshedDetails: Record<string, CoinGeckoAssetData> = {};
    
    for (const assetId of assetIds) {
      const result = await this.fetchAssetDetailsFromApi(assetId);
      if (!(result instanceof Error)) {
        refreshedDetails[assetId] = result;
      }
    }
    
    return refreshedDetails;
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    return await this.apiCache.getStats();
  }

  /**
   * Clear all caches
   */
  async clearCache(): Promise<Response<void>> {
    return await this.apiCache.clearAll();
  }

  /**
   * Fetch with retry logic for handling rate limits and temporary failures
   */
  private async fetchWithRetry(url: string, options: RequestInit = {}, retries = 3, delayMs = 1000): Promise<CoinGeckoAssetData> {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url, options);
        
        if (response.status === 429) {
          // Too many requests, wait and retry
          await new Promise(resolve => setTimeout(resolve, delayMs * (i + 1)));
          continue;
        }
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        if (i === retries - 1) {
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, delayMs * (i + 1)));
      }
    }
    throw new Error('Failed after retries');
  }
}

export const priceService = PriceService.getInstance();
